import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getEnvBaseUrl } from '../../../shared/common/helpers';
import { handleError } from '../../../shared/common/HandleErrors';
import { Axios } from '../../../shared/common/http';
import { RootState } from '../../../shared/store';
import {
    CqmMeasureResultVersionDto,
    initialQualityReportingImportsState,
    QrdaImportRunDto,
    QrdaImportRunsSearchDto,
    QualityReportingImportsState,
} from './imports-models';

// Absolute URL — the request interceptor calls `new URL(config.url)`, which throws on relative paths.
const qrdaImportUrl = (path: string) => `${getEnvBaseUrl()}/api/QrdaImport${path}`;

interface ApiEnvelope<T> {
    succeeded: boolean;
    message?: string;
    entity?: T;
    pagination?: { total?: number };
}

export const uploadImport = createAsyncThunk(
    'qualityReportingImports/upload',
    async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await Axios.post<ApiEnvelope<QrdaImportRunDto>>(qrdaImportUrl('/UploadFile'), formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.data.succeeded && response.data.entity) {
            return response.data.entity;
        }
        handleError({ result: response.data } as any, () => {});
        throw new Error(response.data.message || 'Upload failed');
    },
);

/**
 * Import runs for a picker control, WITHOUT touching slice state — `fetchImportRuns` backs the
 * Imports list screen, so dispatching it from a picker clobbers whatever page/filter that list
 * last loaded. Returns [] on failure: a picker is a convenience, not a reason to break the screen.
 */
export const loadImportRunOptions = async (): Promise<QrdaImportRunDto[]> => {
    try {
        const response = await Axios.post<ApiEnvelope<QrdaImportRunDto[]>>(qrdaImportUrl('/GetRuns'), {
            current: 1,
            pageSize: 50,
        });
        return response.data.succeeded ? (response.data.entity ?? []) : [];
    } catch (err) {
        // Surfaced, not swallowed: a silent empty picker is indistinguishable from "no runs exist".
        // eslint-disable-next-line no-console
        console.error('Failed to load import runs for the scope picker', err);
        return [];
    }
};

export const fetchImportRuns = createAsyncThunk(
    'qualityReportingImports/fetchRuns',
    async (search: QrdaImportRunsSearchDto) => {
        const body = {
            current: search.current ?? 1,
            pageSize: search.pageSize ?? 50,
            status: search.status,
            createdByMe: search.createdByMe,
        };
        const response = await Axios.post<ApiEnvelope<QrdaImportRunDto[]> & { pagination?: { total?: number } }>(
            qrdaImportUrl('/GetRuns'),
            body,
        );
        if (response.data.succeeded) {
            return {
                items: response.data.entity ?? [],
                total: response.data.pagination?.total ?? response.data.entity?.length ?? 0,
            };
        }
        handleError({ result: response.data } as any, () => {});
        return { items: [] as QrdaImportRunDto[], total: 0 };
    },
);

export const fetchImportRun = createAsyncThunk(
    'qualityReportingImports/fetchRun',
    async (id: number) => {
        const response = await Axios.get<ApiEnvelope<QrdaImportRunDto>>(qrdaImportUrl(`/GetRun/${id}`));
        if (response.data.succeeded) {
            return response.data.entity ?? null;
        }
        handleError({ result: response.data } as any, () => {});
        return null;
    },
);

export const retryImportRun = createAsyncThunk(
    'qualityReportingImports/retry',
    async (id: number) => {
        const response = await Axios.post<ApiEnvelope<QrdaImportRunDto>>(qrdaImportUrl(`/RetryRun/${id}`));
        if (response.data.succeeded && response.data.entity) {
            return response.data.entity;
        }
        handleError({ result: response.data } as any, () => {});
        throw new Error(response.data.message || 'Retry failed');
    },
);

export const deleteImportRun = createAsyncThunk(
    'qualityReportingImports/delete',
    async (id: number) => {
        const response = await Axios.delete<ApiEnvelope<string>>(qrdaImportUrl(`/DeleteRun/${id}`));
        if (response.data.succeeded) {
            return id;
        }
        handleError({ result: response.data } as any, () => {});
        throw new Error(response.data.message || 'Delete failed');
    },
);

// S13 Model 1 — versions for a single imported patient×measure. Returned in version-desc
// order; consumed by the per-patient results drawer to render the Provenance JSON section.
// Year is not part of the key: ImportRunPatientId is globally unique and the stored Year
// tracks the QRDA's ReportingPeriod (not the upload calendar year), so passing the upload
// year previously returned empty for decks whose reporting period predated the upload
// (e.g. CMS2v15 decks report on 2024 but were uploaded in 2026).
export const loadImportedVersionsForMeasure = async (
    importRunPatientId: number,
    measureCmsId: string,
): Promise<CqmMeasureResultVersionDto[]> => {
    const response = await Axios.get<ApiEnvelope<CqmMeasureResultVersionDto[]>>(
        qrdaImportUrl(`/GetImportedVersions/${importRunPatientId}/${encodeURIComponent(measureCmsId)}`),
    );
    if (response.data.succeeded) {
        return response.data.entity ?? [];
    }
    handleError({ result: response.data } as any, () => {});
    return [];
};

const slice = createSlice({
    name: 'qualityReportingImports',
    initialState: initialQualityReportingImportsState,
    reducers: {
        clearCurrentImportRun: (state: QualityReportingImportsState) => {
            state.currentRun = null;
        },
        setImportsError: (state: QualityReportingImportsState, action: PayloadAction<string | null>) => {
            state.errorMessage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(uploadImport.pending, (state) => {
                state.processing = true;
                state.errorMessage = null;
            })
            .addCase(uploadImport.fulfilled, (state, action) => {
                state.processing = false;
                state.currentRun = action.payload;
            })
            .addCase(uploadImport.rejected, (state, action) => {
                state.processing = false;
                state.errorMessage = action.error.message ?? 'Upload failed.';
            })
            .addCase(fetchImportRuns.pending, (state) => {
                state.loading = true;
                state.errorMessage = null;
            })
            .addCase(fetchImportRuns.fulfilled, (state, action) => {
                state.loading = false;
                state.runs = action.payload.items;
                state.runsTotal = action.payload.total;
            })
            .addCase(fetchImportRuns.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = action.error.message ?? 'Failed to load imports.';
            })
            .addCase(fetchImportRun.pending, (state) => {
                state.loading = true;
                state.errorMessage = null;
            })
            .addCase(fetchImportRun.fulfilled, (state, action) => {
                state.loading = false;
                state.currentRun = action.payload;
            })
            .addCase(fetchImportRun.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = action.error.message ?? 'Failed to load import.';
            })
            .addCase(retryImportRun.pending, (state) => {
                state.processing = true;
                state.errorMessage = null;
            })
            .addCase(retryImportRun.fulfilled, (state) => {
                state.processing = false;
            })
            .addCase(retryImportRun.rejected, (state, action) => {
                state.processing = false;
                state.errorMessage = action.error.message ?? 'Retry failed.';
            })
            .addCase(deleteImportRun.pending, (state) => {
                state.processing = true;
                state.errorMessage = null;
            })
            .addCase(deleteImportRun.fulfilled, (state) => {
                state.processing = false;
            })
            .addCase(deleteImportRun.rejected, (state, action) => {
                state.processing = false;
                state.errorMessage = action.error.message ?? 'Delete failed.';
            });
    },
});

export const { clearCurrentImportRun, setImportsError } = slice.actions;

export const selectImportRuns = (state: RootState) => (state as any).qualityReportingImports?.runs ?? [];
export const selectImportRunsTotal = (state: RootState) => (state as any).qualityReportingImports?.runsTotal ?? 0;
export const selectCurrentImportRun = (state: RootState) => (state as any).qualityReportingImports?.currentRun ?? null;
export const selectImportsLoading = (state: RootState) => (state as any).qualityReportingImports?.loading ?? false;
export const selectImportsProcessing = (state: RootState) => (state as any).qualityReportingImports?.processing ?? false;
export const selectImportsError = (state: RootState) => (state as any).qualityReportingImports?.errorMessage ?? null;

export default slice.reducer;
